"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useReducedMotion } from "../../hooks/use-reduced-motion";
import { SendGeometry } from "./send-geometry";
import { LoadingGeometry } from "./loading-geometry";
import { ThinkingGeometry } from "./thinking-geometry";
import { preloadChatActionMotion } from "./preload";
import "./send.css";
import "./thinking-orb.static.css";
import "./loading.css";
import "./chat-action-icon.css";

function subscribeVisibility(onChange: () => void) {
  document.addEventListener("visibilitychange", onChange);
  return () => document.removeEventListener("visibilitychange", onChange);
}
function pageVisible() {
  return document.visibilityState === "visible";
}
function serverVisible() {
  return false;
}

export type ChatActionIconState =
  "send" | "stop" | "loading" | "thinking" | "agent";
export interface ChatActionIconProps {
  state: ChatActionIconState;
  size?: number;
  className?: string;
}

export function ChatActionIcon({
  state,
  size = 20,
  className,
}: ChatActionIconProps) {
  const root = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);
  const reducedMotion = useReducedMotion();
  const wantsMotion = state === "send" || state === "thinking";
  const documentVisible = useSyncExternalStore(
    subscribeVisibility,
    pageVisible,
    serverVisible,
  );

  useEffect(() => {
    const element = root.current;
    if (!element || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry) setVisible(entry.isIntersecting);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (wantsMotion && visible && documentVisible && !reducedMotion) {
      void preloadChatActionMotion();
    }
  }, [visible, documentVisible, reducedMotion, wantsMotion]);

  return (
    <span
      ref={root}
      className={`chat-action-icon send-hover ${className ?? ""}`}
      data-state={state}
      aria-hidden="true"
      style={{ width: size, height: size, fontSize: size }}
    >
      <span className="chat-action-send">
        <SendGeometry />
      </span>
      <span className="chat-action-thinking">
        {state === "loading" ? (
          <LoadingGeometry
            animated={visible && documentVisible && !reducedMotion}
          />
        ) : (
          <ThinkingGeometry
            animated={
              state === "thinking" &&
              visible &&
              documentVisible &&
              !reducedMotion
            }
          />
        )}
      </span>
      <span className="chat-action-stop">
        <svg
          style={{ width: "1em", height: "1em" }}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="0.42"
          strokeLinejoin="miter"
        >
          <rect x="5" y="5" width="14" height="14" strokeOpacity="0.64" />
        </svg>
      </span>
    </span>
  );
}
